import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Divider,
  CircularProgress,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
}));

const initialState = {
  customerName: "",
  customerDocument: "",
  customerEmail: "",
  customerPhone: "",
  customerStreet: "",
  customerNumber: "",
  customerComplement: "",
  customerNeighborhood: "",
  customerCity: "Santo André",
  customerState: "SP",
  customerZipCode: "",
  customerCityCode: "3547809",
  serviceValue: "",
  issRate: "5.00",
  description: "",
  consortiumGroup: "",
  consortiumQuota: "",
  consortiumAdmin: "",
  installments: "",
};

const FiscalOrderModal = ({ open, onClose, order }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (order) {
      setFormData({
        customerName: order.customerName || "",
        customerDocument: order.customerDocument || "",
        customerEmail: order.customerEmail || "",
        customerPhone: order.customerPhone || "",
        customerStreet: order.customerStreet || "",
        customerNumber: order.customerNumber || "",
        customerComplement: order.customerComplement || "",
        customerNeighborhood: order.customerNeighborhood || "",
        customerCity: order.customerCity || "Santo André",
        customerState: order.customerState || "SP",
        customerZipCode: order.customerZipCode || "",
        customerCityCode: order.customerCityCode || "3547809",
        serviceValue: order.serviceValue || "",
        issRate: order.issRate || "5.00",
        description: order.description || "",
        consortiumGroup: order.consortiumGroup || "",
        consortiumQuota: order.consortiumQuota || "",
        consortiumAdmin: order.consortiumAdmin || "",
        installments: order.installments || "",
      });
    } else {
      setFormData(initialState);
    }
  }, [order, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Buscar endereço pelo CEP
  const handleCepBlur = async () => {
    const cep = formData.customerZipCode.replace(/\D/g, "");
    if (cep.length !== 8) return;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setFormData((prev) => ({
          ...prev,
          customerStreet: data.logradouro || prev.customerStreet,
          customerNeighborhood: data.bairro || prev.customerNeighborhood,
          customerCity: data.localidade || prev.customerCity,
          customerState: data.uf || prev.customerState,
          customerCityCode: data.ibge || prev.customerCityCode,
        }));
      }
    } catch (err) {
      // Silenciar erro de CEP
    }
  };

  const handleSubmit = async () => {
    if (!formData.customerName) {
      toast.error("Nome do cliente é obrigatório.");
      return;
    }
    if (!formData.customerDocument) {
      toast.error("CPF/CNPJ do cliente é obrigatório.");
      return;
    }
    if (!formData.serviceValue || Number(formData.serviceValue) <= 0) {
      toast.error("Valor do serviço é obrigatório e deve ser maior que zero.");
      return;
    }

    setLoading(true);
    try {
      if (order) {
        await api.put(`/fiscal/orders/${order.id}`, formData);
        toast.success("Pedido atualizado com sucesso!");
      } else {
        await api.post("/fiscal/orders", formData);
        toast.success("Pedido criado com sucesso!");
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao salvar pedido.");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {order ? `Editar Pedido ${order.orderNumber}` : "Novo Pedido Fiscal"}
      </DialogTitle>
      <DialogContent dividers>
        {/* Dados do Cliente */}
        <Typography className={classes.sectionTitle}>Dados do Cliente (Tomador)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nome / Razão Social *"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="CPF / CNPJ *"
              name="customerDocument"
              value={formData.customerDocument}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Telefone"
              name="customerPhone"
              value={formData.customerPhone}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="E-mail"
              name="customerEmail"
              value={formData.customerEmail}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="CEP"
              name="customerZipCode"
              value={formData.customerZipCode}
              onChange={handleChange}
              onBlur={handleCepBlur}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Código Município (IBGE)"
              name="customerCityCode"
              value={formData.customerCityCode}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Logradouro"
              name="customerStreet"
              value={formData.customerStreet}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Número"
              name="customerNumber"
              value={formData.customerNumber}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Complemento"
              name="customerComplement"
              value={formData.customerComplement}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Bairro"
              name="customerNeighborhood"
              value={formData.customerNeighborhood}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Cidade"
              name="customerCity"
              value={formData.customerCity}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="UF"
              name="customerState"
              value={formData.customerState}
              onChange={handleChange}
              variant="outlined"
              size="small"
              inputProps={{ maxLength: 2 }}
            />
          </Grid>
        </Grid>

        {/* Dados do Consórcio */}
        <Typography className={classes.sectionTitle}>Dados do Consórcio</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Administradora"
              name="consortiumAdmin"
              value={formData.consortiumAdmin}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Grupo"
              name="consortiumGroup"
              value={formData.consortiumGroup}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Cota"
              name="consortiumQuota"
              value={formData.consortiumQuota}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Parcelas"
              name="installments"
              type="number"
              value={formData.installments}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>

        {/* Valores */}
        <Typography className={classes.sectionTitle}>Valores e Serviço</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Valor do Serviço (R$) *"
              name="serviceValue"
              type="number"
              value={formData.serviceValue}
              onChange={handleChange}
              variant="outlined"
              size="small"
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Alíquota ISS (%)"
              name="issRate"
              type="number"
              value={formData.issRate}
              onChange={handleChange}
              variant="outlined"
              size="small"
              inputProps={{ step: "0.01", min: "0", max: "100" }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Valor ISS (calculado)"
              value={
                formData.serviceValue && formData.issRate
                  ? `R$ ${((Number(formData.serviceValue) * Number(formData.issRate)) / 100).toFixed(2)}`
                  : "R$ 0,00"
              }
              variant="outlined"
              size="small"
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrição do Serviço"
              name="description"
              value={formData.description}
              onChange={handleChange}
              variant="outlined"
              size="small"
              multiline
              rows={3}
              placeholder="Descrição do serviço prestado (será usada na NFS-e)"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : order ? "Salvar" : "Criar Pedido"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiscalOrderModal;
